import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { checkDuplicateLead, createLead } from '../../api/leads';
import { listLookup } from '../../api/masterData';
import { DuplicateLeadInfo, DuplicateStudentInfo, LeadDetail } from '../../types/lead';
import { DuplicatePersonDialog } from './DuplicatePersonDialog';

interface QuickLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (lead: LeadDetail) => void;
}

interface FormState {
  name: string;
  phone: string;
  email: string;
  schoolName: string;
  fatherOccupation: string;
  fatherPhone: string;
  motherOccupation: string;
  motherPhone: string;
  leadSourceId: number | '';
  /** Ad tracking: the specific advertisement/campaign identifier, distinct
   * from leadSourceId (the channel) — manual entry, optional. */
  adIdName: string;
  /** Optional free-text note saved with the lead (2026-11-03). */
  notes: string;
}

const emptyForm: FormState = {
  name: '',
  phone: '',
  email: '',
  schoolName: '',
  fatherOccupation: '',
  fatherPhone: '',
  motherOccupation: '',
  motherPhone: '',
  leadSourceId: '',
  adIdName: '',
  notes: '',
};

/** Quick Lead correction: the simplified form collects one "Name" field,
 * but Lead.firstName/lastName are both required, non-empty columns (the
 * full Lead Registration Wizard, CSV import, etc. all still depend on
 * that two-column shape) — there is no full-name column to reuse, so
 * this splits on the first space: everything before it is firstName,
 * everything after is lastName. A single-word name (no space) uses that
 * same word for both, so lastName is never sent empty. */
const splitName = (fullName: string): { firstName: string; lastName: string } => {
  const trimmed = fullName.trim().replace(/\s+/g, ' ');
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return { firstName: trimmed, lastName: trimmed };
  return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
};

/** Loosely mirrors the backend's normalizePhoneForComparison digit-count —
 * just enough to stop the "Check Existing Person" button firing on
 * obviously incomplete input, not a full phone-format validator. */
const hasEnoughDigitsToCheck = (phone: string) => phone.replace(/\D/g, '').length >= 9;

interface DuplicateResult {
  phone: string;
  leads: DuplicateLeadInfo[];
  students: DuplicateStudentInfo[];
}

/**
 * Quick Lead: a fast, minimal-fields alternative to the full Lead
 * Registration Wizard — creates a normal Lead record via the same
 * POST /leads the wizard uses, tagged with whatever Lead Source the
 * Counselor actually selects. Quick Lead is an ENTRY METHOD, not a Lead
 * Source, and must never be stored as one.
 *
 * Duplicate-check state machine (do not weaken without re-reading the
 * spec this implements): Save is only ever enabled once the *currently
 * entered* phone number has been successfully checked against BOTH
 * Leads and registered Students with no match found. Editing the phone
 * after a check immediately invalidates that result (phoneAlreadyChecked
 * below goes false the instant form.phone diverges from checkedPhone) —
 * the backend re-checks mandatorily at submit time regardless, this is
 * purely about not letting the user guess why Save is disabled.
 */
export const QuickLeadDialog = ({ open, onClose, onSaved }: QuickLeadDialogProps) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [checkedPhone, setCheckedPhone] = useState<string | null>(null);
  // Tied to the phone it was found for, not just "the last result we
  // saw" — so editing the phone field invalidates it via phoneAlreadyChecked
  // without needing a separate reset call.
  const [duplicate, setDuplicate] = useState<DuplicateResult | null>(null);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);

  const leadSourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources', { activeOnly: true }),
    enabled: open,
  });

  const checkDuplicateMutation = useMutation({
    mutationFn: (phone: string) => checkDuplicateLead(phone),
    onSuccess: (result, phone) => {
      setCheckedPhone(phone);
      const hasMatch = result.leads.length > 0 || result.students.length > 0;
      setDuplicate(hasMatch ? { phone, leads: result.leads, students: result.students } : null);
      if (hasMatch) setShowDuplicatePopup(true);
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const { firstName, lastName } = splitName(form.name);
      return createLead({
        firstName,
        lastName,
        phone: form.phone,
        email: form.email || undefined,
        schoolName: form.schoolName || undefined,
        fatherOccupation: form.fatherOccupation || undefined,
        fatherPhone: form.fatherPhone || undefined,
        motherOccupation: form.motherOccupation || undefined,
        motherPhone: form.motherPhone || undefined,
        leadSourceId: form.leadSourceId || undefined,
        adIdName: form.adIdName || undefined,
        notes: form.notes.trim() || undefined,
      });
    },
    onSuccess: (lead) => {
      setForm(emptyForm);
      setCheckedPhone(null);
      setDuplicate(null);
      setShowDuplicatePopup(false);
      setError(null);
      onSaved(lead);
    },
    onError: (err) => {
      // The mandatory backend duplicate check can still fire here (a
      // duplicate created between the advisory check above and this
      // submit) — show the exact same popup, not a raw error string.
      const backendDuplicate = isAxiosError(err) ? err.response?.data?.errors : undefined;
      if (backendDuplicate?.duplicateLeads || backendDuplicate?.duplicateStudents) {
        setDuplicate({
          phone: form.phone,
          leads: backendDuplicate.duplicateLeads ?? [],
          students: backendDuplicate.duplicateStudents ?? [],
        });
        setShowDuplicatePopup(true);
        return;
      }
      setError(isAxiosError(err) ? err.response?.data?.message ?? 'Something went wrong' : 'Something went wrong');
    },
  });

  const handleClose = () => {
    setForm(emptyForm);
    setError(null);
    setCheckedPhone(null);
    setDuplicate(null);
    setShowDuplicatePopup(false);
    onClose();
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const phoneTrimmed = form.phone.trim();
  const nameFilled = !!form.name.trim();
  const canAttemptCheck = nameFilled && hasEnoughDigitsToCheck(phoneTrimmed) && !checkDuplicateMutation.isPending;

  const phoneAlreadyChecked = checkedPhone === phoneTrimmed && phoneTrimmed.length > 0;
  const hasDuplicateForCurrentPhone = phoneAlreadyChecked && duplicate?.phone === phoneTrimmed;
  const checkFailed = checkDuplicateMutation.isError;

  const canSave =
    nameFilled &&
    !!phoneTrimmed &&
    !!form.leadSourceId &&
    phoneAlreadyChecked &&
    !hasDuplicateForCurrentPhone &&
    !checkFailed;

  const runCheck = () => {
    if (canAttemptCheck) checkDuplicateMutation.mutate(phoneTrimmed);
  };

  const handleSave = async () => {
    if (!canSave) return;
    await createMutation.mutateAsync();
  };

  const saveDisabledReason = (() => {
    if (!nameFilled || !phoneTrimmed) return null; // required-field asterisks already explain this
    if (checkDuplicateMutation.isPending) return null; // "Checking…" already communicates this
    if (checkFailed) return null; // the error alert below already explains this
    if (hasDuplicateForCurrentPhone) return null; // the warning banner below already explains this
    if (!phoneAlreadyChecked) return 'Check for an existing Lead or Student before saving.';
    if (!form.leadSourceId) return 'Select a Lead Source before saving.';
    return null;
  })();

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Lead</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Name"
                required
                fullWidth
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone Number"
                required
                fullWidth
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                onBlur={runCheck}
                helperText={!phoneAlreadyChecked && phoneTrimmed && !checkDuplicateMutation.isPending ? 'Not checked yet' : ' '}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Email" fullWidth value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="School"
                fullWidth
                value={form.schoolName}
                onChange={(e) => set('schoolName', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Father Profession"
                fullWidth
                value={form.fatherOccupation}
                onChange={(e) => set('fatherOccupation', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Father Contact Number"
                fullWidth
                value={form.fatherPhone}
                onChange={(e) => set('fatherPhone', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Mother Profession"
                fullWidth
                value={form.motherOccupation}
                onChange={(e) => set('motherOccupation', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Mother Contact Number"
                fullWidth
                value={form.motherPhone}
                onChange={(e) => set('motherPhone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Lead Source"
                required
                fullWidth
                value={form.leadSourceId}
                onChange={(e) => set('leadSourceId', e.target.value ? Number(e.target.value) : '')}
              >
                <MenuItem value="">
                  <em>Select…</em>
                </MenuItem>
                {leadSourcesQuery.data?.map((source) => (
                  <MenuItem key={source.id as number} value={source.id as number}>
                    {String(source.name)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ad ID / Name"
                fullWidth
                helperText="Which specific advertisement generated this lead (optional)"
                value={form.adIdName}
                onChange={(e) => set('adIdName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                placeholder="Note"
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 5000 }}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Grid>
          </Grid>

          <Stack sx={{ mt: 1 }} spacing={1}>
            {checkDuplicateMutation.isPending && <Alert severity="info">Checking for an existing Lead or Student…</Alert>}
            {checkFailed && (
              <Alert severity="error">Unable to check for existing leads/students. Please try again.</Alert>
            )}
            {phoneAlreadyChecked && !hasDuplicateForCurrentPhone && !checkDuplicateMutation.isPending && (
              <Alert severity="success">No existing Lead or Student found. You can create this lead.</Alert>
            )}
            {hasDuplicateForCurrentPhone && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => setShowDuplicatePopup(true)}>
                    View details
                  </Button>
                }
              >
                This phone number already belongs to an existing Lead or Student. This lead cannot be created.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button disabled={!canAttemptCheck} onClick={runCheck}>
              {checkDuplicateMutation.isPending ? 'Checking…' : 'Check Existing Person'}
            </Button>
            <Button variant="contained" disabled={!canSave || createMutation.isPending} onClick={handleSave}>
              {createMutation.isPending ? 'Saving…' : 'Save Quick Lead'}
            </Button>
          </Stack>
          {saveDisabledReason && (
            <FormHelperText sx={{ textAlign: 'right', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {saveDisabledReason}
              </Typography>
            </FormHelperText>
          )}
        </DialogActions>
      </Dialog>

      <DuplicatePersonDialog
        open={showDuplicatePopup}
        leads={duplicate?.leads ?? []}
        students={duplicate?.students ?? []}
        onClose={() => setShowDuplicatePopup(false)}
      />
    </>
  );
};
